import React, { useEffect, useState } from 'react';
import { LANGS, t, metalLabel, loadLang, saveLang } from './hsLabels';
import { MetalBadge, TypeBadge } from './hsBadges';   // 5.18.0: same badges as the plan list
import { fs } from './hsScale';   // 5.31.0: font sizes scale with the page's Text size

// =============================================================================
// HealthSherpa customer-facing screens (5.16.0): the large plan view ("turn the screen toward the
// customer") and Compare (up to 4). Labels come from hsLabels.js in the chosen language; carrier text
// (plan names, copays, network names) is shown exactly as HealthSherpa sends it. The estimate footnote
// is always visible; a $0.00 net premium always carries the *.
// =============================================================================

const usd = (v) => (v === null || v === undefined || Number.isNaN(Number(v)))
  ? '—'
  : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const netText = (v) => (v !== null && v !== undefined && Math.abs(Number(v)) < 0.005) ? `${usd(v)}*` : usd(v);
const amount = (v, text) => (v !== null && v !== undefined) ? usd(v) : (text || '—');

// One label per plan colour; colour is never the only cue (each bar carries the logo + name).
const BAR_COLORS = ['#2563eb', '#d97706', '#059669', '#9333ea'];

export function LogoTile({ url, name, height = 28, maxWidth = 140, darkMode, pad = '3px 6px' }) {
  if (!url) return <span style={{ fontWeight: 600 }}>{name || '—'}</span>;
  const img = <img src={url} alt={name || ''} style={{ maxHeight: height, maxWidth, objectFit: 'contain', display: 'block' }} />;
  // Never recolor: in dark mode the logo sits on a small white rounded tile.
  return darkMode
    ? <span style={{ display: 'inline-block', background: '#fff', borderRadius: 6, padding: pad, lineHeight: 0 }}>{img}</span>
    : <span style={{ display: 'inline-block', lineHeight: 0 }}>{img}</span>;
}

export function useLang() {
  const [lang, setLangState] = useState(loadLang);
  const setLang = (code) => { setLangState(code); saveLang(code); };
  return [lang, setLang];
}

function LangToggle({ lang, setLang, darkMode }) {
  return (
    <div role="group" aria-label="Language" style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #888' }}>
      {LANGS.map((l) => (
        <button key={l.code} onClick={() => setLang(l.code)} aria-pressed={lang === l.code}
          style={{ padding: '8px 14px', fontSize: fs(20), border: 'none', cursor: 'pointer', background: lang === l.code ? '#185FA5' : (darkMode ? '#242424' : '#fff'), color: lang === l.code ? '#fff' : (darkMode ? '#f0f0f0' : '#1a1a1a') }}>
          {l.label}
        </button>
      ))}
    </div>
  );
}

function ScopeTabs({ scope, setScope, lang, darkMode }) {
  return (
    <div role="tablist" style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #888' }}>
      {['individual', 'family'].map((k) => (
        <button key={k} role="tab" aria-selected={scope === k} onClick={() => setScope(k)}
          style={{ padding: '8px 18px', fontSize: fs(20), border: 'none', cursor: 'pointer', background: scope === k ? '#185FA5' : (darkMode ? '#242424' : '#fff'), color: scope === k ? '#fff' : (darkMode ? '#f0f0f0' : '#1a1a1a') }}>
          {t(k, lang)}
        </button>
      ))}
    </div>
  );
}

const ded = (p, scope) => (scope === 'family' ? amount(p.deductible_family, p.deductible_family_text) : amount(p.deductible_individual, p.deductible_individual_text));
const moop = (p, scope) => (scope === 'family' ? amount(p.moop_family, p.moop_family_text) : amount(p.moop_individual, p.moop_individual_text));
const dedNum = (p, scope) => (scope === 'family' ? p.deductible_family : p.deductible_individual);
const moopNum = (p, scope) => (scope === 'family' ? p.moop_family : p.moop_individual);

function useColors(darkMode) {
  return {
    bg: darkMode ? '#141414' : '#ffffff', panel: darkMode ? '#202020' : '#f6f8fb',
    text: darkMode ? '#f5f5f5' : '#111', sub: darkMode ? '#bbb' : '#444', border: darkMode ? '#3a3a3a' : '#d6dbe3',
  };
}

function Footnote({ lang, c }) {
  return <div style={{ fontSize: fs(20), color: c.sub, padding: '14px 28px', borderTop: `1px solid ${c.border}` }}>{t('footnote', lang)}</div>;
}

function useKeys(handlers) {
  useEffect(() => {
    const onKey = (e) => { const h = handlers[e.key]; if (h) { e.preventDefault(); h(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
}

const DOC_KEYS = ['summary_of_benefits', 'brochure', 'formulary', 'provider_network', 'payment'];

// --- large plan view ---------------------------------------------------------------------------------

export function PlanView({ plans, index, setIndex, onClose, selectedIds, onToggle, darkMode }) {
  const c = useColors(darkMode);
  const [lang, setLang] = useLang();
  const [scope, setScope] = useState('individual');
  const n = plans.length;
  const p = plans[Math.max(0, Math.min(index, n - 1))];
  const prev = () => n && setIndex((index - 1 + n) % n);
  const next = () => n && setIndex((index + 1) % n);
  useKeys({ Escape: onClose, ArrowLeft: prev, ArrowRight: next });
  if (!p) return null;
  const picked = selectedIds.includes(p.plan_id);
  const docs = p.documents || {};
  const big = { fontSize: fs(22) };
  return (
    <div role="dialog" aria-label="Plan" style={{ position: 'fixed', inset: 0, zIndex: 1000, background: c.bg, color: c.text, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 28px', borderBottom: `1px solid ${c.border}` }}>
        <button onClick={prev} style={{ fontSize: fs(22), padding: '8px 16px', cursor: 'pointer' }} aria-label={t('previous', lang)}>‹ {t('previous', lang)}</button>
        <span style={{ fontSize: fs(20), color: c.sub }}>{index + 1} / {n}</span>
        <button onClick={next} style={{ fontSize: fs(22), padding: '8px 16px', cursor: 'pointer' }} aria-label={t('next', lang)}>{t('next', lang)} ›</button>
        <span style={{ flex: 1 }} />
        <LangToggle lang={lang} setLang={setLang} darkMode={darkMode} />
        <button onClick={onClose} style={{ fontSize: fs(22), padding: '8px 18px', cursor: 'pointer' }}>{t('close', lang)} ✕</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <LogoTile url={p.logo_url} name={p.issuer_name} height={72} maxWidth={280} darkMode={darkMode} />
          <div>
            <div style={{ fontSize: fs(32), fontWeight: 700 }}>{p.name}</div>
            <div style={{ fontSize: fs(22), color: c.sub }}>
              <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5em' }}>
                {p.logo_url && <span>{p.issuer_name}</span>}
                <MetalBadge code={p.metal_level} label={metalLabel(p.metal_level, lang)} darkMode={darkMode} />
                <TypeBadge code={p.plan_type} darkMode={darkMode} />
                {p.network_name && <span>{p.network_name}</span>}
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 40, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 26, padding: 22, background: c.panel, borderRadius: 12 }}>
          <div>
            <div style={{ fontSize: fs(22), color: c.sub }}>{t('monthly_cost', lang)}</div>
            <div style={{ fontSize: fs(64), fontWeight: 800, lineHeight: 1.1 }}>{netText(p.net_premium)}</div>
          </div>
          <div style={big}>
            <div>{t('premium_before_credit', lang)}: <strong>{usd(p.gross_premium)}</strong></div>
            <div>{t('tax_credit', lang)}: <strong>{usd(p.subsidy_applied)}</strong></div>
          </div>
        </div>
        <div style={{ marginTop: 22 }}><ScopeTabs scope={scope} setScope={setScope} lang={lang} darkMode={darkMode} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18, marginTop: 16 }}>
          <div style={{ padding: 18, background: c.panel, borderRadius: 12 }}>
            <div style={{ fontSize: fs(22), color: c.sub }}>{t('deductible', lang)} ({t(scope, lang)})</div>
            <div style={{ fontSize: fs(36), fontWeight: 700 }}>{ded(p, scope)}</div>
          </div>
          <div style={{ padding: 18, background: c.panel, borderRadius: 12 }}>
            <div style={{ fontSize: fs(22), color: c.sub }}>{t('moop', lang)} ({t(scope, lang)})</div>
            <div style={{ fontSize: fs(36), fontWeight: 700 }}>{moop(p, scope)}</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginTop: 18 }}>
          {[['primary_care', p.primary_care], ['specialist', p.specialist], ['urgent_care', p.urgent_care], ['generic_drugs', p.generic_rx]].map(([k, v]) => (
            <div key={k} style={{ padding: 16, border: `1px solid ${c.border}`, borderRadius: 10 }}>
              <div style={{ fontSize: fs(20), color: c.sub }}>{t(k, lang)}</div>
              <div style={{ fontSize: fs(24), fontWeight: 600 }}>{v || '—'}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap', marginTop: 22, fontSize: fs(20) }}>
          {p.hsa_eligible === true && <span>{t('hsa_eligible', lang)}: {t('yes', lang)}</span>}
          <strong>{t('plan_documents', lang)}:</strong>
          {DOC_KEYS.filter((k) => docs[k]).map((k) => (
            <a key={k} href={docs[k]} target="_blank" rel="noopener noreferrer" style={{ color: '#4a9eda', fontSize: fs(20) }}>{t(`doc_${k}`, lang)} ↗</a>
          ))}
          {DOC_KEYS.every((k) => !docs[k]) && <span>—</span>}
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 12, fontSize: fs(22), marginTop: 24, cursor: 'pointer' }}>
          <input type="checkbox" checked={picked} onChange={() => onToggle(p.plan_id)} style={{ width: 26, height: 26 }} />
          {t('add_to_quote', lang)}
        </label>
      </div>
      <Footnote lang={lang} c={c} />
    </div>
  );
}

// --- compare (2–4) ---------------------------------------------------------------------------------

// 5.16.2: three compact boxes (monthly cost / deductible / out-of-pocket max), each a small VERTICAL column
// chart — one column per plan, same order and colours as the cards. Columns start at 0; the tallest value in a
// box fills CHART_H; a missing value shows "—" with no column. Under every column: logo tile (or the carrier
// name, never truncated) and the plan name (at most 2 lines), so colour is never the only cue.
const CHART_H = 220;
const BOX_W = 360;

function ColumnBox({ title, plans, value, label, c, darkMode }) {
  const vals = plans.map(value).map((v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v)));
  const max = Math.max(0, ...vals.filter((v) => v !== null));
  return (
    <div style={{ flex: `0 1 ${BOX_W}px`, minWidth: 260, border: `1px solid ${c.border}`, borderRadius: 12, padding: '14px 12px', background: c.panel }}>
      <div style={{ fontSize: fs(20), fontWeight: 700, marginBottom: 10, textAlign: 'center' }}>{title}</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: CHART_H + 30, borderBottom: `2px solid ${c.sub}` }}>
        {plans.map((p, i) => (
          <div key={p.plan_id} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', minWidth: 0 }}>
            <div style={{ fontSize: fs(18), fontWeight: 800, marginBottom: 4, whiteSpace: 'nowrap' }}>{label(p)}</div>
            {vals[i] !== null && (
              <div title={label(p)} style={{ width: '70%', maxWidth: 64, height: max > 0 ? Math.round((vals[i] / max) * CHART_H) : 0,
                minHeight: vals[i] > 0 ? 2 : 0, background: BAR_COLORS[i % 4], borderRadius: '4px 4px 0 0' }} />
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        {plans.map((p, i) => (
          <div key={p.plan_id} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, textAlign: 'center' }}>
            <span aria-hidden style={{ width: 12, height: 12, borderRadius: 3, background: BAR_COLORS[i % 4] }} />
            {p.logo_url
              ? <LogoTile url={p.logo_url} name={p.issuer_name} height={22} maxWidth={78} darkMode={darkMode} />
              : <span style={{ fontSize: fs(13), fontWeight: 700, overflowWrap: 'anywhere' }}>{p.issuer_name || '—'}</span>}
            <span title={p.name} style={{ fontSize: fs(13), lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>{p.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CompareView({ plans, onClose, darkMode }) {
  const c = useColors(darkMode);
  const [lang, setLang] = useLang();
  const [scope, setScope] = useState('individual');
  useKeys({ Escape: onClose });
  const cols = `repeat(${plans.length}, minmax(220px, 1fr))`;
  const cell = { padding: '12px 14px', borderTop: `1px solid ${c.border}` };
  const rowLabel = (k) => <div style={{ fontSize: fs(20), color: c.sub, gridColumn: `1 / -1`, padding: '12px 14px 0' }}>{k}</div>;
  return (
    <div role="dialog" aria-label={t('compare_plans', lang)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: c.bg, color: c.text, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 28px', borderBottom: `1px solid ${c.border}` }}>
        <span style={{ fontSize: fs(28), fontWeight: 700 }}>{t('compare_plans', lang)}</span>
        <ScopeTabs scope={scope} setScope={setScope} lang={lang} darkMode={darkMode} />
        <span style={{ flex: 1 }} />
        <LangToggle lang={lang} setLang={setLang} darkMode={darkMode} />
        <button onClick={onClose} style={{ fontSize: fs(22), padding: '8px 18px', cursor: 'pointer' }}>{t('close', lang)} ✕</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 28px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: cols, columnGap: 12, background: c.panel, borderRadius: 12, paddingBottom: 12 }}>
          {plans.map((p, i) => (
            <div key={p.plan_id} style={{ padding: 14, borderTop: `6px solid ${BAR_COLORS[i % 4]}`, borderRadius: '12px 12px 0 0' }}>
              <LogoTile url={p.logo_url} name={p.issuer_name} height={48} maxWidth={200} darkMode={darkMode} />
              <div style={{ fontSize: fs(24), fontWeight: 700, marginTop: 8 }}>{p.name}</div>
              <div style={{ fontSize: fs(20), color: c.sub, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5em', marginTop: 4 }}>
                {p.logo_url && <span>{p.issuer_name}</span>}
                <MetalBadge code={p.metal_level} label={metalLabel(p.metal_level, lang)} darkMode={darkMode} />
                <TypeBadge code={p.plan_type} darkMode={darkMode} />
              </div>
            </div>
          ))}
          {rowLabel(t('monthly_cost', lang))}
          {plans.map((p) => <div key={p.plan_id} style={{ ...cell, fontSize: fs(48), fontWeight: 800 }}>{netText(p.net_premium)}</div>)}
          {rowLabel(`${t('deductible', lang)} (${t(scope, lang)})`)}
          {plans.map((p) => <div key={p.plan_id} style={{ ...cell, fontSize: fs(26), fontWeight: 700 }}>{ded(p, scope)}</div>)}
          {rowLabel(`${t('moop', lang)} (${t(scope, lang)})`)}
          {plans.map((p) => <div key={p.plan_id} style={{ ...cell, fontSize: fs(26), fontWeight: 700 }}>{moop(p, scope)}</div>)}
          {[['primary_care', 'primary_care'], ['specialist', 'specialist'], ['urgent_care', 'urgent_care'], ['generic_drugs', 'generic_rx']].map(([k, f]) => (
            <React.Fragment key={k}>
              {rowLabel(t(k, lang))}
              {plans.map((p) => <div key={p.plan_id} style={{ ...cell, fontSize: fs(20) }}>{p[f] || '—'}</div>)}
            </React.Fragment>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center', maxWidth: 1200, margin: '22px auto 0' }}>
          <ColumnBox title={t('monthly_cost', lang)} plans={plans} value={(p) => p.net_premium} label={(p) => netText(p.net_premium)} c={c} darkMode={darkMode} />
          <ColumnBox title={`${t('deductible', lang)} (${t(scope, lang)})`} plans={plans} value={(p) => dedNum(p, scope)} label={(p) => ded(p, scope)} c={c} darkMode={darkMode} />
          <ColumnBox title={`${t('moop', lang)} (${t(scope, lang)})`} plans={plans} value={(p) => moopNum(p, scope)} label={(p) => moop(p, scope)} c={c} darkMode={darkMode} />
        </div>
      </div>
      <Footnote lang={lang} c={c} />
    </div>
  );
}

// Staff-facing: more than 4 plans ticked -> choose which 4 to compare.
export function CompareChooser({ plans, onCompare, onClose, darkMode }) {
  const [ids, setIds] = useState(plans.slice(0, 4).map((p) => p.plan_id));
  const toggle = (id) => setIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= 4 ? cur : [...cur, id]));
  const bg = darkMode ? '#242424' : '#fff';
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: bg, color: darkMode ? '#f0f0f0' : '#1a1a1a', borderRadius: 8, padding: 18, width: 520, maxWidth: '92vw', maxHeight: '80vh', overflowY: 'auto' }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Choose up to 4 plans to compare ({ids.length}/4)</div>
        {plans.map((p) => (
          <label key={p.plan_id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', fontSize: fs(14) }}>
            <input type="checkbox" checked={ids.includes(p.plan_id)} disabled={!ids.includes(p.plan_id) && ids.length >= 4} onChange={() => toggle(p.plan_id)} />
            <span>{p.issuer_name} — {p.name}</span>
          </label>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
          <button onClick={onClose}>Cancel</button>
          <button disabled={ids.length < 2} onClick={() => onCompare(plans.filter((p) => ids.includes(p.plan_id)))}>Compare</button>
        </div>
      </div>
    </div>
  );
}
